"use client";

import { c as _c } from "react/compiler-runtime";
import { useRouter } from "next/navigation";
import { createContext, use, useOptimistic, useRef } from "react";
import { cartSetQuantityAction } from "@/actions/cart";
import { assertUnreachable, invariant } from "@/lib/utils";
const CartContextProvider = createContext(null);
export const CartContext = t0 => {
  const $ = _c(6);
  const {
    children,
    cart
  } = t0;
  const [optimisticCart, dispatchCartAction] = useOptimistic(cart, _temp);
  let t1;
  if ($[0] !== dispatchCartAction || $[1] !== optimisticCart) {
    t1 = {
      cart: optimisticCart,
      dispatch: dispatchCartAction
    };
    $[0] = dispatchCartAction;
    $[1] = optimisticCart;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== children || $[4] !== t1) {
    t2 = <CartContextProvider.Provider value={t1}>{children}</CartContextProvider.Provider>;
    $[3] = children;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
};
export const useCart = () => {
  const value = use(CartContextProvider);
  invariant(value, "`useCart` must be used within a `CartContext`");
  return value;
};
export const useChangeQuantity = qty => {
  const router = useRouter();
  const stateRef = useRef(null);
  const formAction = async props => {
    const doWork = async () => {
      try {
        const modifier = props.type === "INCREASE" ? 1 : -1;
        const newQuantity = props.type === "UPDATE" ? props.quantity : qty + modifier;
        await cartSetQuantityAction({
          lineId: props.lineId,
          quantity: newQuantity
        });
        router.refresh();
        stateRef.current?.promise.resolve();
      } catch (error) {
        stateRef.current?.promise.reject(error);
      } finally {
        stateRef.current = null;
      }
    };
    if (stateRef.current) {
      clearTimeout(stateRef.current.timer ?? undefined);
      stateRef.current.timer = setTimeout(doWork, 400);
    } else {
      stateRef.current = {
        timer: setTimeout(doWork, 400),
        promise: Promise.withResolvers()
      };
    }
    return stateRef.current.promise.promise;
  };
  return {
    formAction,
    isPending: !!stateRef.current
  };
};
function _temp(state, action) {
  if (!state) {
    return state;
  }
  switch (action.type) {
    case "UPDATE":
      {
        return state?.lineItems.find(line_6 => line_6.id === action.lineId)?.quantity ? {
          ...state,
          lineItems: state.lineItems.map(line_5 => {
            if (line_5.id === action.lineId) {
              return {
                ...line_5,
                quantity: action.quantity
              };
            }
            return line_5;
          })
        } : state;
      }
    case "INCREASE":
      {
        return state?.lineItems.find(line_4 => line_4.id === action.lineId)?.quantity ? {
          ...state,
          lineItems: state.lineItems.map(line_3 => {
            if (line_3.id === action.lineId) {
              return {
                ...line_3,
                quantity: line_3.quantity + 1
              };
            }
            return line_3;
          })
        } : state;
      }
    case "DECREASE":
      {
        return state?.lineItems.find(line_2 => line_2.id === action.lineId)?.quantity ? {
          ...state,
          lineItems: state.lineItems.map(line_1 => {
            if (line_1.id === action.lineId) {
              if (line_1.quantity - 1 <= 0) {
                return null;
              }
              return {
                ...line_1,
                quantity: line_1.quantity - 1
              };
            }
            return line_1;
          }).filter(Boolean)
        } : state;
      }
    case "ADD_ITEM":
      {
        const actionSubscriptionId = action.subscription?.id || null;
        return state?.lineItems.find(line_0 => line_0.productVariantId === action.variant.id && line_0.subscriptionId === actionSubscriptionId) ? {
          ...state,
          lineItems: state.lineItems.map(line => {
            if (line.productVariantId === action.variant.id && line.subscriptionId === actionSubscriptionId) {
              return {
                ...line,
                quantity: line.quantity + 1
              };
            }
            return line;
          })
        } : {
          ...state,
          lineItems: state.lineItems.concat({
            id: global.crypto.randomUUID(),
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            cartId: state.id,
            productVariantId: action.variant.id,
            quantity: 1,
            productVariant: {
              ...action.variant,
              product: action.product
            },
            subscriptionId: actionSubscriptionId,
            subscription: action.subscription
          })
        };
      }
  }
  assertUnreachable(action);
  return state;
}